<template>
    <div class="chart-container" v-if="sensorChartValues && show">
        <div class="chart-content">
            <div class="sensor-name-container" :class="$mq">
                <div class="sensor-name-container-content">
                    <template>
                        <div v-if="sensorStatus == 'working'" class="i-andy-menu-checklist i-andy-m icon-status working"></div>
                        <div v-if="sensorStatus == 'issue'" class="i-andy-exclamation i-andy-m icon-status issue"></div>
                        <div v-if="sensorStatus == 'alert'" class="i-andy-menu-task i-andy-m icon-status alert"></div>
                        <div v-if="sensorStatus == 'nodata'" class="i-andy-no-wifi i-andy-m icon-status nodata"></div>
                        <div v-if="sensorStatus == 'offline'" class="i-andy-bell-disabled i-andy-m icon-status offline"></div>
                    </template>
                    <div v-if="sensor.ref != sensor.name" class="box-sensor-name">
                        <div class="sensor-name">{{ sensor.name }}</div>
                        <div class="sensor-ref">{{ sensor.ref }}</div>
                    </div>
                    <span v-else class="sensor-name">{{ sensor.name }}</span>
                </div>
                <div v-if="configDisabled == true" class="alert-configuration online">
                    <div class="i-andy-info-main i-andy-m"></div>
                    <span><b>{{ $t('sensor.alert_no_config') }}</b></span>
                </div>
                <div v-else class="alert-configuration" :class="{ online: alertEnabled, offline: !alertEnabled }">
                    <div class="i-andy-info-main i-andy-m"></div>
                    <span v-if="alertEnabled && sensor.testo_product_reset_at" v-html="$t('sensor.alert_stabilizing', { time: formatDate(sensor.testo_product_reset_at) })" class="sensor-alert-stabilizing"></span>
                    <span v-else-if="alertEnabled" v-html="$t(alertOnKey)"></span>
                    <span v-else v-html="$t(alertOffKey)"></span>
                    <label class="switch" v-if="sensor.config">
                        <input type="checkbox" :checked="alertEnabled" @change="onAlertToggleChange($event)" />
                        <div class="slider round"></div>
                    </label>
                </div>
            </div>
            <div class="sensor-value-container">
                <span class="sensor-value">
                    <span v-if="isHumidity" class="sensor-value-humidity">
                        <i class="i-andy-droplets i-andy-m"></i>
                        <span class="sensor-value-humidity-text">
                            {{ displayLastValue }}<span class="sensor-value-humidity-unit">&nbsp;{{ sensorUnit }}</span>
                        </span>
                    </span>
                    <template v-else>
                        {{ displayLastValue }}
                    </template>
                </span>
                <span class="sensor-value-unit">{{ acceptableRange }}</span>
            </div>
            <div class="alert-mode-toggles">
                <div class="alert-mode-tabs">
                    <span>{{ $t('sensor.show_issues')}}</span>
                    <button class="tab" :class="{ active: alertMode === 'lines' }" @click="toggleMode('lines')">{{ $t('sensor.chart_show_lines') }}</button>
                    <button class="tab" :class="{ active: alertMode === 'ranges' }" @click="toggleMode('ranges')">{{ $t('sensor.chart_show_ranges') }}</button>

                    <!-- <div class="chart-settings-dropdown" :class="{ active: showSettingsDropdown }">
                        <button class="settings-trigger" @click="toggleSettingsDropdown">
                            <div class="three-dots-vertical">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </button>
                        <div class="settings-menu" v-if="showSettingsDropdown">
                            <label class="checkbox-container">
                                <input type="checkbox" v-model="enableAnimations" @change="toggleAnimations" />
                                <span class="checkmark"></span>
                                <span class="checkbox-label">{{ $t('sensor.chart_animations') }}</span>
                            </label>
                            <label class="checkbox-container">
                                <input type="checkbox" v-model="hideGradient" @change="toggleGradient" />
                                <span class="checkmark"></span>
                                <span class="checkbox-label">{{ $t('sensor.chart_gradient') }}</span>
                            </label>
                            <label class="checkbox-container">
                                <input type="checkbox" v-model="showGrid" @change="toggleGrid" />
                                <span class="checkmark"></span>
                                <span class="checkbox-label">{{ $t('sensor.chart_grid') }}</span>
                            </label>
                            <label class="checkbox-container">
                                <input type="checkbox" v-model="reverseYAxis" @change="toggleReverseYAxis" />
                                <span class="checkmark"></span>
                                <span class="checkbox-label">{{ $t('sensor.chart_reverse_y_axis') }}</span>
                            </label>
                        </div>
                    </div> -->
                </div>
            </div>
            <div class="history-container">
                <div class="date-container">
                    <span class="history">{{ chartHistoryLabel }}</span>
                    <span v-if="sensor.last_value && sensor.last_value.date" class="last-value">{{ $t('sensor.chart_last_value') }} {{ formatDate(sensor.last_value.date) }}</span>
                </div>
            </div>
        </div>
        <div
            ref="chartPlot"
            class="sensor-chart-plot"
            :class="{ 'is-zoomed': isZoomed }"
        >
            <button
                v-if="isZoomed"
                type="button"
                class="chart-reset-zoom"
                :aria-label="$t('sensor.chart_reset_zoom')"
                @click="resetChartZoom"
            >
                {{ $t('sensor.chart_reset_zoom') }}
            </button>
            <ChartRenderer ref="chartRenderer" :chartKey="chartKey" :chartOptions="chartOptions" :series="series" />
        </div>
    </div>
    <div class="chart-container" v-else>
        <div class="greybox"></div>
    </div>
</template>

<script>
import { formatDisplayDateFromStore } from '@/utils/dateFormatters'
import i18n from '@/i18n'
import { formatDecimal, formatTemperature, temperatureUnitSuffix } from '@/utils/numberFormat'
import { isHumiditySensor } from '@/utils/sensorStatus'
import ChartRenderer from './ChartRenderer.vue'
import {
    constrainZoomRange,
    getAnchorRatioFromClientX,
    getChartTimestampsMs,
    getDatetimeFormatter,
    getMinZoomWindowMs,
    getMomentFormatForSpan,
    getPinchDistance,
    getTimeSpanForRange,
    getTooltipMomentFormatForSpan,
    getTouchesMidX,
    isSameRange,
    resolveFilterRangeMs,
    zoomRangeAround
} from '@/utils/sensorChartZoom'

export default {
    name: 'SensorChart',
    components: {
        ChartRenderer
    },
    props: {
        sensor: {
            type: Object,
            required: true
        },
        sensorChartValues: {
            type: [Object, String, Boolean],
            default: false
        },
        sensorType: {
            type: [String, Boolean],
            default: 'dotbox'
        },
        sensorStatus: {
            type: String,
            default: 'working'
        },
        dateFilter: {
            type: Object,
            default: null
        },
        hoveredAlertTimestamp: {
            type: [Number, String, Boolean],
            default: null
        },
        selectedAlertTimestamp: {
            type: [Number, String, Boolean],
            default: null
        },
        sensorHistory: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            show: false,
            chartKey: 1,
            hasAmbientSeries: false,
            sendValueCharts: false,
            alertMode: 'lines', // Puede ser: 'lines', 'ranges' o null
            gridPosition: 'back',
            enableAnimations: true,
            showSettingsDropdown: false,
            hideGradient: true,
            showGrid: false,
            reverseYAxis: false,
            alertEnabled: false,
            configDisabled: false,
            gapBridges: [],
            isZoomed: false,
            visibleXRange: null,
            touchGesture: null,
            touchRafId: null,
            pendingTouchZoom: null,
            chartPlotEl: null,
            chartOptions: {
                chart: {
                    id: this.sensor.id,
                    toolbar: {
                        show: false,
                        autoSelected: 'zoom'
                    },
                    zoom: {
                        enabled: true,
                        type: 'x',
                        autoScaleYaxis: false
                    },
                    selection: {
                        enabled: true,
                        type: 'x'
                    },
                    crosshair: {
                        show: false
                    },
                    background: '#fff'
                },
                grid: {
                    show: true,
                    position: 'back',
                    padding: {
                        left: 24,
                        right: 24
                    }
                },
                tooltip: {
                    x: {
                        show: true,
                        format: 'dd MMM yyyy - HH:mm'
                    },
                    y: {
                        formatter: (value) => {
                            if (value === null || value === undefined || isNaN(Number(value))) {
                                return ''
                            }
                            return this.formatMeasurement(value, 2)
                        }
                    }
                },
                xaxis: {
                    categories: [],
                    hideOverlappingLabels: true,
                    style: {
                        fontSize: '20px'
                    }
                },
                noData: {
                    text: i18n.t('sensor.no_data'),
                    align: 'center',
                    verticalAlign: 'middle',
                    offsetX: 0,
                    offsetY: 0,
                    style: {
                        color: '#000',
                        fontSize: '14px',
                        fontFamily: 'Avenir-Regular'
                    }
                },
                legend: {
                    show: false
                },
                stroke: {
                    curve: 'straight',
                    width: 2
                },
                dataLabels: {
                    enabled: false
                }
            }
        }
    },
    computed: {
        isHumidity() {
            return isHumiditySensor(this.sensor)
        },
        sensorUnitSuffix() {
            return this.isHumidity ? '%' : temperatureUnitSuffix('°C', this.$i18n.locale)
        },
        chartMarkerUnit() {
            return this.isHumidity ? '%' : temperatureUnitSuffix('°C', this.$i18n.locale)
        },
        sensorUnit() {
            return this.isHumidity ? this.$t('sensor.grades_humidity') : '°C'
        },
        displayLastValue() {
            if (this.sensorStatus === 'nodata') {
                return '-'
            }

            if (!this.sensor.last_value || this.sensor.last_value.value == null) {
                return '-'
            }

            return this.formatMeasurement(this.sensor.last_value.value)
        },
        chartHistoryLabel() {
            return this.isHumidity ? this.$t('sensor.chart_history_humidity') : this.$t('sensor.chart_history')
        },
        alertOnKey() {
            return this.isHumidity ? 'sensor.alert_on_humidity' : 'sensor.alert_on'
        },
        alertOffKey() {
            return this.isHumidity ? 'sensor.alert_off_humidity' : 'sensor.alert_off'
        },
        hasDualTemperatureSeries() {
            return !this.isHumidity && (this.sensorType === 'testo' || this.sensorType === 'rmoni')
        },
        series() {
            if (!this.show) {
                return []
            }
            if (this.sendValueCharts) {
                let data = []
                let ambientData = []
                let serie = []
                let sensor = this.sensorChartValues

                if (sensor && typeof sensor != 'string') {
                    if (Object.keys(sensor).length > 0) {
                        Object.keys(sensor).forEach((timestamp) => {
                            let value = sensor[timestamp]
                            let timestampMs = parseInt(timestamp) * 1000

                            // Validar que timestamp y value sean números válidos
                            if (!isNaN(timestampMs) && value && typeof value.value === 'number' && !isNaN(value.value)) {
                                data.push({ x: timestampMs, y: value.value })

                                // detectar valor ambiental para sensores testo y rmoni
                                if (this.hasDualTemperatureSeries) {
                                    let ambientValue = typeof value.ambient !== 'undefined' ? value.ambient : typeof value.aambiente !== 'undefined' ? value.aambiente : null
                                    if (ambientValue !== null && ambientValue !== undefined && !isNaN(parseFloat(ambientValue))) {
                                        ambientData.push({ x: timestampMs, y: parseFloat(ambientValue) })
                                    }
                                }
                            }
                        })

                        data.sort((a, b) => a.x - b.x)

                        const seriesName = this.hasDualTemperatureSeries ? 'Producto' : this.sensor.name

                        if (this.gapBridges.length > 0) {
                            const mainData = []
                            const dashedData = []
                            const gapStartSet = new Set(this.gapBridges.map(b => b.from.x))

                            for (let i = 0; i < data.length; i++) {
                                mainData.push(data[i])
                                if (gapStartSet.has(data[i].x) && i < data.length - 1) {
                                    mainData.push({ x: data[i].x + 1000, y: null })
                                }
                            }

                            this.gapBridges.forEach((bridge, idx) => {
                                if (idx > 0) {
                                    dashedData.push({ x: bridge.from.x - 1000, y: null })
                                }
                                dashedData.push({ x: bridge.from.x, y: bridge.from.y })
                                dashedData.push({ x: bridge.to.x, y: bridge.to.y })
                            })

                            serie.push({
                                name: seriesName,
                                data: mainData,
                                color: '#008FFB'
                            })

                            serie.push({
                                name: seriesName + ' (estimado)',
                                data: dashedData,
                                color: '#b3b3b3'
                            })
                        } else {
                            serie.push({
                                name: seriesName,
                                data: data,
                                color: '#008FFB'
                            })
                        }

                        // Serie ambiental opcional en verde si existe al menos un dato
                        if (this.hasDualTemperatureSeries && ambientData.some((v) => v.y !== null && v.y !== undefined && !isNaN(parseFloat(v.y)))) {
                            serie.push({
                                name: 'Ambiente',
                                data: ambientData,
                                color: '#00E396'
                            })
                        }
                    }

                    // Las anotaciones de yaxis ahora se configuran en updateAxis
                }
                return serie
            }
            return []
        },
        acceptableRange() {
            if (!this.sensor.config) {
                return ''
            }

            const metric = this.isHumidity ? 'humidity' : 'temperature'

            switch (this.sensor.config.condition) {
                case '<':
                    return this.$t(`sensor.chart_${metric}_higher_than`) + ' ' + this.formatReading(this.sensor.config.value) + this.sensorUnitSuffix

                case '>':
                    return this.$t(`sensor.chart_${metric}_lower_than`) + ' ' + this.formatReading(this.sensor.config.value) + this.sensorUnitSuffix

                case '<>': {
                    const [v1, v2] = this.sensor.config.value.split(',')
                    const value1 = this.formatMeasurement(parseFloat(v1))
                    const value2 = this.formatMeasurement(parseFloat(v2))

                    return this.$t(`sensor.chart_${metric}_not_in_range`, { value1, value2 })
                }

                case '!<>': {
                    const [v1, v2] = this.sensor.config.value.split(',')
                    const value1 = this.formatMeasurement(parseFloat(v1))
                    const value2 = this.formatMeasurement(parseFloat(v2))

                    return this.$t(`sensor.chart_${metric}_range`, { value1, value2 })
                }

                default:
                    return ''
            }
        },
        userAppAccess() {
            return this.$store.getters['loginUser/getHasAccessApp']
        },
        loggedUser() {
            return this.userAccess ? this.userAccess : this.$store.getters['loginUser/getLocalEmployee']
        },
        user() {
            if (this.loggedUser) {
                return this.$store.getters['employee/getEmployee'](this.loggedUser)
            }
            return false
        }
    },
    methods: {
        formatMeasurement(value, digits) {
            return this.isHumidity ? formatDecimal(value, this.$i18n.locale, digits) : formatTemperature(value, this.$i18n.locale, '°C', { maximumFractionDigits: digits === undefined ? 2 : digits })
        },
        formatReading(value) {
            const formatted = formatDecimal(value, i18n.locale)
            return formatted === '' ? '-' : formatted
        },
        chartFinish() {
            this.sendValueCharts = true
        },
        detectGaps() {
            this.gapBridges = []
            if (!this.sensorChartValues || typeof this.sensorChartValues === 'string') return

            const sortedPoints = []
            Object.keys(this.sensorChartValues).forEach((timestamp) => {
                const value = this.sensorChartValues[timestamp]
                const timestampMs = parseInt(timestamp) * 1000
                if (!isNaN(timestampMs) && value && typeof value.value === 'number' && !isNaN(value.value)) {
                    sortedPoints.push({ x: timestampMs, y: value.value })
                }
            })

            sortedPoints.sort((a, b) => a.x - b.x)
            if (sortedPoints.length < 3) return

            const intervals = []
            for (let i = 1; i < sortedPoints.length; i++) {
                intervals.push(sortedPoints[i].x - sortedPoints[i - 1].x)
            }

            const sortedIntervals = [...intervals].sort((a, b) => a - b)
            const medianInterval = sortedIntervals[Math.floor(sortedIntervals.length / 2)]
            const MIN_GAP_MS = 15 * 60 * 1000
            const gapThreshold = Math.max(medianInterval * 1.5, MIN_GAP_MS)

            const bridges = []
            for (let i = 1; i < sortedPoints.length; i++) {
                if (sortedPoints[i].x - sortedPoints[i - 1].x > gapThreshold) {
                    bridges.push({
                        from: { x: sortedPoints[i - 1].x, y: sortedPoints[i - 1].y },
                        to: { x: sortedPoints[i].x, y: sortedPoints[i].y }
                    })
                }
            }

            this.gapBridges = bridges
        },
        onAlertToggleChange(event) {
            const target = event && event.target ? event.target : null
            const nextStatus = target ? !!target.checked : !this.alertEnabled

            if (target) {
                target.checked = this.alertEnabled
            }
            const customReasons = {
                101: { id: '101', name: 'answer.justify.cleaning' },
                102: { id: '102', name: 'answer.justify.broken' },
                103: { id: '103', name: 'answer.justify.unavailable' }
            }

            const data = {
                showErrorReason: false,
                errorReason: this.$t('answer.justify.error')
            }

            const self = this
            const kitchenMode = localStorage.kitchenMode == null || localStorage.kitchenMode === 'true'
            const userLogged = this.$store.getters['loginUser/getLocalEmployee']

            if (kitchenMode && !userLogged) {
                if (!nextStatus) {
                    this.$popup.justify({
                        title: this.$t('answer.justify.justify_sensor'),
                        description: this.$t('answer.justify.explain_reason_sensor'),
                        textSave: this.$t('popup.access.text_save'),
                        textCancel: this.$t('popup.confirm.text_cancel'),
                        customClass: 'sensorchart-justify',
                        data: data,
                        list: customReasons,
                        callSave: async function (popup, store) {
                            // Capture justify popup data before closing it
                            const justifySelected = popup.selected
                            const justifyMessage = popup.message || ''
                            const justifyData = { ...popup.data }

                            self.$store.commit('loginUser/setRefreshLayout', false)
                            await self.$popup.close()
                            self.$popup.login({
                                list: store.getters['employee/getLoginUsers'](0),
                                callAfterAccess: function (response) {
                                    self.$overlay.loading()
                                    if (!justifySelected || (justifySelected == 0 && justifyMessage.trim() == '')) {
                                        justifyData.showErrorReason = true
                                        self.$overlay.hide()
                                        self.$popup.close()
                                    } else {
                                        self.$popup.close()
                                        self.$store
                                            .dispatch('sensor/activateSensorAlert', {
                                                sensorId: self.sensor.id,
                                                employeeId: self.$store.getters['loginUser/getLocalEmployee'],
                                                status: nextStatus,
                                                reason: JSON.stringify(customReasons[justifySelected]),
                                                description: justifyMessage
                                            })
                                            .then(function () {
                                                self.alertEnabled = nextStatus
                                                self.$overlay.hide()
                                                self.$emit('sensor-alert-changed', nextStatus)
                                            })
                                    }
                                    self.$store.commit('loginUser/setRefreshLayout', true)
                                }
                            })
                        },
                        callCancel: function () {
                            self.$popup.close()
                        }
                    })
                } else {
                    self.$store.commit('loginUser/setRefreshLayout', false)
                    self.$popup.login({
                        list: self.$store.getters['employee/getLoginUsers'](0),
                        callAfterAccess: function (response) {
                            self.$overlay.loading()
                            self.$popup.close()
                            self.$store
                                .dispatch('sensor/activateSensorAlert', {
                                    sensorId: self.sensor.id,
                                    employeeId: self.$store.getters['loginUser/getLocalEmployee'],
                                    status: nextStatus
                                })
                                .then(function () {
                                    self.alertEnabled = nextStatus
                                    self.$overlay.hide()
                                    self.$emit('sensor-alert-changed', nextStatus)
                                })
                            self.$store.commit('loginUser/setRefreshLayout', true)
                        }
                    })
                }
            } else {
                if (!self.userAppAccess) {
                    this.$popup.alert({ message: this.$t('answer.no_access_app_sign', { name: self.user.name }) })
                } else {
                    if (!nextStatus) {
                        this.$popup.justify({
                            title: this.$t('answer.justify.justify_sensor'),
                            description: this.$t('answer.justify.explain_reason_sensor'),
                            textSave: this.$t('popup.access.text_save'),
                            textCancel: this.$t('popup.confirm.text_cancel'),
                            customClass: 'sensorchart-justify',
                            data: data,
                            list: customReasons,
                            callSave: function (popup, store) {
                                self.$overlay.loading()
                                if (!popup.selected || (popup.selected == 0 && (popup.message || '').trim() == '')) {
                                    popup.data.showErrorReason = true
                                    self.$overlay.hide()
                                } else {
                                    popup.data.showErrorReason = false
                                    store
                                        .dispatch('sensor/activateSensorAlert', {
                                            sensorId: self.sensor.id,
                                            employeeId: self.$store.getters['loginUser/getLocalEmployee'],
                                            status: nextStatus,
                                            reason: JSON.stringify(customReasons[popup.selected]),
                                            description: popup.message || ''
                                        })
                                        .then(function () {
                                            self.alertEnabled = nextStatus
                                            self.$popup.close()
                                            self.$overlay.hide()
                                            self.$emit('sensor-alert-changed', nextStatus)
                                        })
                                }
                            },
                            callCancel: function () {
                                self.$popup.close()
                            }
                        })
                    } else {
                        self.$overlay.loading()
                        self.$store
                            .dispatch('sensor/activateSensorAlert', {
                                sensorId: self.sensor.id,
                                employeeId: self.$store.getters['loginUser/getLocalEmployee'],
                                status: nextStatus
                            })
                            .then(function () {
                                self.alertEnabled = nextStatus
                                self.$overlay.hide()
                                self.$emit('sensor-alert-changed', nextStatus)
                            })
                    }
                }
            }
        },
        calculateTimeSpan() {
            const range = this.getActiveXRange()
            const span = getTimeSpanForRange(range.min, range.max)

            if (span === 'months') {
                return 'monthly'
            }
            if (span === 'days') {
                return 'daily'
            }
            return 'hourly'
        },
        getDateFormat() {
            const range = this.getActiveXRange()
            const span = getTimeSpanForRange(range.min, range.max)
            const momentFormat = getMomentFormatForSpan(span)

            return {
                format: momentFormat,
                datetimeFormatter: getDatetimeFormatter(),
                momentFormat,
                tooltipFormat: getTooltipMomentFormatForSpan(span)
            }
        },
        getFullXRange() {
            const fromFilter = resolveFilterRangeMs(this.dateFilter)
            if (fromFilter) {
                return fromFilter
            }

            const timestamps = getChartTimestampsMs(this.sensorChartValues)
            if (timestamps.length === 0) {
                const now = Date.now()
                return { min: now - 24 * 60 * 60 * 1000, max: now }
            }

            return {
                min: timestamps[0],
                max: timestamps[timestamps.length - 1]
            }
        },
        getActiveXRange() {
            return this.visibleXRange || this.getFullXRange()
        },
        getNativeMinZoomWindow() {
            const full = this.getFullXRange()
            return getMinZoomWindowMs(getChartTimestampsMs(this.sensorChartValues), full.max - full.min)
        },
        constrainVisibleRange(min, max) {
            const full = this.getFullXRange()
            return constrainZoomRange(min, max, full.min, full.max, this.getNativeMinZoomWindow())
        },
        setVisibleRange(min, max) {
            const full = this.getFullXRange()
            const next = this.constrainVisibleRange(min, max)
            this.visibleXRange = next
            this.isZoomed = !isSameRange(next, full)
            return next
        },
        resetZoomState() {
            this.cancelTouchAnimation()
            this.touchGesture = null
            this.visibleXRange = null
            this.isZoomed = false
        },
        applyChartZoom(min, max) {
            const next = this.setVisibleRange(min, max)
            const renderer = this.$refs.chartRenderer
            if (renderer && typeof renderer.zoomX === 'function') {
                renderer.zoomX(next.min, next.max)
            }
            return next
        },
        resetChartZoom() {
            const full = this.getFullXRange()
            this.resetZoomState()
            const renderer = this.$refs.chartRenderer
            if (renderer && typeof renderer.zoomX === 'function') {
                renderer.zoomX(full.min, full.max)
                return
            }
            this.updateChartOptions()
        },
        handleBeforeZoom(chartContext, limits) {
            const xaxis = limits && limits.xaxis ? limits.xaxis : null
            if (!xaxis) {
                return
            }
            const next = this.constrainVisibleRange(xaxis.min, xaxis.max)
            return { xaxis: next }
        },
        handleBeforeResetZoom() {
            this.resetZoomState()
            return { xaxis: this.getFullXRange() }
        },
        handleChartZoomed(chartContext, limits) {
            const xaxis = limits && limits.xaxis ? limits.xaxis : null
            if (!xaxis) {
                return
            }
            const next = this.setVisibleRange(xaxis.min, xaxis.max)
            if (!this.touchGesture) {
                this.syncAxisRangeOptions(next)
            }
        },
        handleChartScrolled(chartContext, limits) {
            const xaxis = limits && limits.xaxis ? limits.xaxis : null
            if (!xaxis) {
                return
            }
            this.setVisibleRange(xaxis.min, xaxis.max)
        },
        syncAxisRangeOptions(range) {
            if (!this.chartOptions || !this.chartOptions.xaxis || !range) {
                return
            }

            const currentRange = {
                min: this.chartOptions.xaxis.min,
                max: this.chartOptions.xaxis.max
            }
            const mode = this.isZoomed ? 'pan' : 'zoom'
            const currentMode = this.chartOptions.chart && this.chartOptions.chart.toolbar
                ? this.chartOptions.chart.toolbar.autoSelected
                : 'zoom'

            if (isSameRange(currentRange, range) && currentMode === mode) {
                return
            }

            const copy = { ...this.chartOptions }
            copy.xaxis = {
                ...copy.xaxis,
                min: range.min,
                max: range.max
            }
            copy.chart = {
                ...copy.chart,
                toolbar: {
                    show: false,
                    autoSelected: mode
                }
            }
            this.chartOptions = copy
        },
        onChartWheel(event) {
            if (!this.canInteractWithChart()) {
                return
            }

            const rect = event.currentTarget.getBoundingClientRect()
            if (rect.width <= 0) {
                return
            }

            if (event.ctrlKey || event.metaKey) {
                event.preventDefault()
                const current = this.getActiveXRange()
                const full = this.getFullXRange()
                const factor = event.deltaY > 0 ? 1.2 : 0.83
                const next = zoomRangeAround(
                    current.min,
                    current.max,
                    factor,
                    getAnchorRatioFromClientX(event.clientX, rect),
                    full.min,
                    full.max,
                    this.getNativeMinZoomWindow()
                )
                this.applyChartZoom(next.min, next.max)
                return
            }

            if (!this.isZoomed) {
                return
            }

            event.preventDefault()
            const current = this.getActiveXRange()
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
            const shift = ((current.max - current.min) * delta) / rect.width
            const next = this.constrainVisibleRange(current.min + shift, current.max + shift)
            this.applyChartZoom(next.min, next.max)
        },
        canInteractWithChart() {
            return !!(this.sensorChartValues && typeof this.sensorChartValues !== 'string')
        },
        beginPinchGesture(target, touches) {
            const startDistance = getPinchDistance(touches)
            if (startDistance <= 0) {
                return
            }

            this.touchGesture = {
                type: 'pinch',
                startDistance,
                startRange: { ...this.getActiveXRange() },
                anchorRatio: getAnchorRatioFromClientX(getTouchesMidX(touches), target.getBoundingClientRect())
            }
        },
        beginPanGesture(touch) {
            this.touchGesture = {
                type: 'pan',
                startX: touch.clientX,
                startRange: { ...this.getActiveXRange() }
            }
        },
        queueTouchZoom(min, max) {
            this.pendingTouchZoom = { min, max }
            if (this.touchRafId != null) {
                cancelAnimationFrame(this.touchRafId)
            }

            this.touchRafId = requestAnimationFrame(() => {
                this.touchRafId = null
                this.flushTouchZoom()
            })
        },
        flushTouchZoom() {
            const pending = this.pendingTouchZoom
            this.pendingTouchZoom = null
            if (this.touchRafId != null) {
                cancelAnimationFrame(this.touchRafId)
                this.touchRafId = null
            }
            if (!pending) {
                return
            }

            this.applyChartZoom(pending.min, pending.max)
        },
        cancelTouchAnimation() {
            if (this.touchRafId != null) {
                cancelAnimationFrame(this.touchRafId)
                this.touchRafId = null
            }
            this.pendingTouchZoom = null
        },
        updatePinchGesture(touches) {
            const gesture = this.touchGesture
            const distance = getPinchDistance(touches)
            if (!gesture || gesture.startDistance <= 0 || distance <= 0) {
                return
            }

            const full = this.getFullXRange()
            const next = zoomRangeAround(
                gesture.startRange.min,
                gesture.startRange.max,
                gesture.startDistance / distance,
                gesture.anchorRatio,
                full.min,
                full.max,
                this.getNativeMinZoomWindow()
            )
            this.queueTouchZoom(next.min, next.max)
        },
        updatePanGesture(target, touch) {
            const gesture = this.touchGesture
            const rect = target.getBoundingClientRect()
            if (!gesture || rect.width <= 0) {
                return
            }

            const shift = ((gesture.startRange.max - gesture.startRange.min) * (gesture.startX - touch.clientX)) / rect.width
            const next = this.constrainVisibleRange(gesture.startRange.min + shift, gesture.startRange.max + shift)
            this.queueTouchZoom(next.min, next.max)
        },
        endTouchGesture() {
            this.flushTouchZoom()

            const hadGesture = !!this.touchGesture
            this.touchGesture = null
            if (hadGesture) {
                this.syncAxisRangeOptions(this.getActiveXRange())
            }
        },
        unbindChartGestureListeners() {
            const el = this.chartPlotEl
            if (!el) {
                return
            }

            el.removeEventListener('wheel', this.onChartWheel, true)
            el.removeEventListener('touchstart', this.onChartTouchStart, true)
            el.removeEventListener('touchmove', this.onChartTouchMove, true)
            el.removeEventListener('touchend', this.onChartTouchEnd, true)
            el.removeEventListener('touchcancel', this.onChartTouchEnd, true)
            this.chartPlotEl = null
        },
        bindChartGestureListeners() {
            const el = this.$refs.chartPlot
            if (!el) {
                this.unbindChartGestureListeners()
                return
            }

            if (el === this.chartPlotEl) {
                return
            }

            this.unbindChartGestureListeners()
            this.chartPlotEl = el
            el.addEventListener('wheel', this.onChartWheel, { passive: false, capture: true })
            el.addEventListener('touchstart', this.onChartTouchStart, { passive: false, capture: true })
            el.addEventListener('touchmove', this.onChartTouchMove, { passive: false, capture: true })
            el.addEventListener('touchend', this.onChartTouchEnd, { passive: false, capture: true })
            el.addEventListener('touchcancel', this.onChartTouchEnd, { passive: false, capture: true })
        },
        onChartTouchStart(event) {
            if (!this.canInteractWithChart()) {
                return
            }

            if (event.touches.length >= 2) {
                event.preventDefault()
                this.beginPinchGesture(event.currentTarget, event.touches)
                return
            }

            if (event.touches.length === 1 && this.isZoomed) {
                this.beginPanGesture(event.touches[0])
            }
        },
        onChartTouchMove(event) {
            if (!this.touchGesture || !this.canInteractWithChart()) {
                return
            }

            if (this.touchGesture.type === 'pinch' && event.touches.length >= 2) {
                event.preventDefault()
                this.updatePinchGesture(event.touches)
                return
            }

            if (this.touchGesture.type === 'pan' && event.touches.length === 1) {
                event.preventDefault()
                this.updatePanGesture(event.currentTarget, event.touches[0])
            }
        },
        onChartTouchEnd(event) {
            if (event.touches.length >= 2) {
                this.beginPinchGesture(event.currentTarget, event.touches)
                return
            }

            if (event.touches.length === 1 && this.isZoomed) {
                this.beginPanGesture(event.touches[0])
                return
            }

            this.endTouchGesture()
        },
        xAxisValues() {
            let min = 9000
            let max = -1000
            let hasAmbient = false
            let hasAlerts = false
            let hasTwoAlerts = false
            let alertVals = []

            if (this.sensorChartValues && typeof this.sensorChartValues != 'string') {
                for (let key in this.sensorChartValues) {
                    const value = this.sensorChartValues[key]
                    if (value && typeof value.value === 'number' && !isNaN(value.value)) {
                        if (value.value < min) {
                            min = value.value
                        }
                        if (value.value > max) {
                            max = value.value
                        }
                    }
                    if (this.hasDualTemperatureSeries && (typeof value.ambient !== 'undefined' || typeof value.aambiente !== 'undefined')) {
                        hasAmbient = true
                        const ambientRaw = typeof value.ambient !== 'undefined' ? value.ambient : value.aambiente
                        const ambientNum = parseFloat(ambientRaw)
                        if (!isNaN(ambientNum)) {
                            if (ambientNum < min) min = ambientNum
                            if (ambientNum > max) max = ambientNum
                        }
                    }
                }

                if (this.sensor && this.sensor.config && this.sensor.config.value != null) {
                    if (typeof this.sensor.config.value === 'string') {
                        alertVals = this.sensor.config.value
                            .split(',')
                            .map((v) => parseFloat(v))
                            .filter((v) => !isNaN(v))
                        if (alertVals.length === 2) hasTwoAlerts = true
                    } else {
                        const val = parseFloat(this.sensor.config.value)
                        if (!isNaN(val)) alertVals.push(val)
                    }
                    if (alertVals.length) hasAlerts = true
                }

                alertVals.forEach((v) => {
                    if (v < min) min = v
                    if (v > max) max = v
                })

                // Validar que min y max sean números válidos
                if (!isNaN(min) && !isNaN(max) && min !== 9000 && max !== -1000) {
                    min = Math.round(min) - 1
                    max = Math.round(max) + 1
                    this.hasAmbientSeries = hasAmbient
                    this.detectGaps()
                    this.updateAxis(min, max, hasAmbient, hasAlerts, hasTwoAlerts)
                } else {
                    this.gapBridges = []
                    this.updateAxis(0, 10, hasAmbient, hasAlerts, hasTwoAlerts)
                }
            } else if (this.sensorChartValues == 'nodata') {
                this.show = true
            }
        },
        updateAxis(min, max, hasAmbient, hasAlerts, hasTwoAlerts) {
            let self = this
            let copy = { ...this.chartOptions }

            const dateFormat = this.getDateFormat()
            const activeRange = this.getActiveXRange()

            copy.xaxis = {
                type: 'datetime',
                min: activeRange.min,
                max: activeRange.max,
                labels: {
                    show: true,
                    rotate: 0,
                    rotateAlways: false,
                    hideOverlappingLabels: true,
                    datetimeUTC: false,
                    formatter: function (value) {
                        const numericValue = typeof value === 'string' ? parseFloat(value) : Number(value)
                        if (isNaN(numericValue)) {
                            return ''
                        }
                        const range = self.getActiveXRange()
                        const span = getTimeSpanForRange(range.min, range.max)
                        return formatDisplayDateFromStore(moment(numericValue), { style: 'chart', includeTime: span !== 'days' && span !== 'months' })
                    },
                    format: dateFormat.format,
                    datetimeFormatter: dateFormat.datetimeFormatter,
                    style: {
                        fontSize: '12px',
                        fontFamily: 'Avenir-Regular'
                    }
                },
                tickAmount: 6
            }
            copy.legend = {
                show: this.hasAmbientSeries
            }

            // Aplicar configuración del grid
            copy.grid = {
                show: this.showGrid,
                position: 'back',
                padding: {
                    left: 24,
                    right: 24
                }
            }

            // Calcular los 5 puntos del eje Y: min-2, puntos intermedios, max+2

            const yMin = min
            const yMax = max
            //console.log(min, max, yMin, yMax)
            copy.yaxis = {
                min: yMin > yMax ? yMax : yMin,
                max: yMin > yMax ? yMin : yMax,
                forceNiceScale: false, // Desactivar para control manual
                tickAmount: 4, // 4 intervalos = 5 puntos
                convertedCatToNumeric: false,
                reversed: this.reverseYAxis, // Invertir el eje Y según la opción del usuario
                labels: {
                    show: true,
                    formatter: (value) => {
                        if (value === null || value === undefined || isNaN(Number(value))) {
                            return ''
                        }
                        return this.formatMeasurement(value, 0)
                    }
                }
            }

            let strokeWidths = [3]
            let fillOpacities = [1]
            let fillTypes = ['solid']
            let dashArray = [0]
            let colors = ['#008FFB']

            if (this.gapBridges.length > 0) {
                strokeWidths.push(2)
                fillOpacities.push(1)
                fillTypes.push('solid')
                dashArray.push(3)
                colors.push('#b3b3b3')
            }

            if (hasAmbient) {
                strokeWidths.push(3)
                fillOpacities.push(0.8)
                fillTypes.push('solid')
                dashArray.push(0)
                colors.push('#00E396')
            }
            if (hasAlerts) {
                strokeWidths.push(0.5)
                fillOpacities.push(0)
                fillTypes.push('solid')
                dashArray.push(0)
                colors.push('#BD1F1E')
                if (hasTwoAlerts) {
                    strokeWidths.push(0.5)
                    fillOpacities.push(0)
                    fillTypes.push('solid')
                    dashArray.push(0)
                    colors.push('#BD1F1E')
                }
            }
            copy.colors = colors
            copy.stroke = {
                curve: 'straight',
                width: strokeWidths,
                dashArray: dashArray
            }

            copy.fill = {
                type: fillTypes,
                opacity: fillOpacities,
                /* gradient: {
                    type: 'vertical',
                    shade: 'light',
                    shadeIntensity: 0.5,
                    gradientToColors: ['#fff'],
                    opacityFrom: 0.8,
                    opacityTo: 0.1,
                    gradientStops: [0, 100]
                } */
            }

            copy.dataLabels = {
                enabled: false
            }

            const alertAnnotations = this.getAlertAnnotations()

            // Obtener anotaciones de yaxis (rangos críticos) si existen
            let yAnnotations = []
            if (this.sensor && this.sensor.config && this.sensor.config.value != null) {
                let alertValues = []
                if (typeof this.sensor.config.value === 'string') {
                    alertValues = this.sensor.config.value
                        .split(',')
                        .map((v) => parseFloat(v))
                        .filter((v) => !isNaN(v))
                } else {
                    const val = parseFloat(this.sensor.config.value)
                    if (!isNaN(val)) alertValues.push(val)
                }

                if (alertValues.length === 2) {
                    const low = Math.min(alertValues[0], alertValues[1])
                    const high = Math.max(alertValues[0], alertValues[1])
                    const condition = this.sensor && this.sensor.config ? this.sensor.config.condition : null

                    // For '<>' and '!<>' we now draw threshold LINES (low/high) instead of a shaded band.
                    // This avoids painting the whole area between values (or towards infinity for '!<>').
                    if (condition === '<>' || condition === '!<>') {
                        const makeLine = (y, withLabel) => ({
                            y,
                            borderColor: 'rgba(189, 31, 30, 0.8)',
                            strokeDashArray: 0,
                            zIndex: 10,
                            ...(withLabel
                                ? {
                                      label: {
                                          text: this.$t('sensor.chart_critical_range'),
                                          borderColor: 'transparent',
                                          style: {
                                              color: 'rgba(189, 31, 30, 0.8)',
                                              background: 'none'
                                          }
                                      }
                                  }
                                : {})
                        })

                        // Put the label on the upper line to avoid duplicated "Critical range" labels.
                        yAnnotations = [makeLine(low, false), makeLine(high, true)]
                    } else {
                        // Fallback: if some other operator uses 2 values, keep a band.
                        yAnnotations = [
                            {
                                y: low,
                                y2: high,
                                borderColor: 'rgba(189, 31, 30, 0.3)',
                                fillColor: 'rgba(189, 31, 30, 0.3)',
                                opacity: 0.3,
                                label: {
                                    text: this.$t('sensor.chart_critical_range'),
                                    borderColor: 'transparent',
                                    style: {
                                        color: 'rgba(189, 31, 30, 0.8)',
                                        background: 'none'
                                    }
                                }
                            }
                        ]
                    }
                } else {
                    yAnnotations = alertValues.map((v) => ({
                        y: v,
                        borderColor: 'rgba(189, 31, 30, 0.8)',
                        strokeDashArray: 0,
                        zIndex: 10,
                        label: {
                            text: this.$t('sensor.chart_critical_limit'),
                            borderColor: 'transparent',
                            style: {
                                color: 'rgba(189, 31, 30, 0.8)',
                                background: 'none'
                            }
                        }
                    }))
                }
            }

            copy.annotations = {
                points: alertAnnotations.points,
                xaxis: alertAnnotations.xaxis,
                yaxis: [...yAnnotations]
            }

            copy.tooltip = {
                ...(copy.tooltip || {}),
                x: {
                    show: true,
                    formatter: function (value) {
                        const numericValue = typeof value === 'string' ? parseFloat(value) : Number(value)
                        if (isNaN(numericValue)) {
                            return ''
                        }
                        return formatDisplayDateFromStore(moment(numericValue), { includeTime: true })
                    }
                }
            }

            copy.chart = {
                ...copy.chart,
                toolbar: {
                    show: false,
                    autoSelected: this.isZoomed ? 'pan' : 'zoom'
                },
                zoom: {
                    enabled: true,
                    type: 'x',
                    autoScaleYaxis: false
                },
                selection: {
                    enabled: true,
                    type: 'x'
                },
                animations: {
                    enabled: this.enableAnimations
                },
                events: {
                    mounted: function () {
                        self.chartFinish()
                    },
                    beforeZoom: function (chartContext, limits) {
                        return self.handleBeforeZoom(chartContext, limits)
                    },
                    beforeResetZoom: function () {
                        return self.handleBeforeResetZoom()
                    },
                    zoomed: function (chartContext, limits) {
                        self.handleChartZoomed(chartContext, limits)
                    },
                    scrolled: function (chartContext, limits) {
                        self.handleChartScrolled(chartContext, limits)
                    }
                }
            }

            this.sendValueCharts = true
            this.chartOptions = copy
            this.show = true
        },
        normalizeAlertMarkerTimestamp(timestamp) {
            if (timestamp === null || typeof timestamp === 'undefined' || timestamp === false || timestamp === '') {
                return null
            }

            const numericTimestamp = Number(timestamp)

            if (isNaN(numericTimestamp) || numericTimestamp <= 0) {
                return null
            }

            return numericTimestamp > 1e12 ? numericTimestamp : numericTimestamp * 1000
        },
        getMarkerPointAtTimestamp(timestampMs) {
            if (!timestampMs || !this.sensorChartValues || typeof this.sensorChartValues === 'string') {
                return null
            }

            const targetTimestampMs = Number(timestampMs)

            if (isNaN(targetTimestampMs)) {
                return null
            }

            const exactKey = String(Math.round(targetTimestampMs / 1000))
            const exactValue = this.sensorChartValues[exactKey]

            if (exactValue && typeof exactValue.value === 'number' && !isNaN(exactValue.value)) {
                return { x: targetTimestampMs, y: exactValue.value }
            }

            let nearest = null

            Object.entries(this.sensorChartValues).forEach(([timestamp, valueData]) => {
                const chartTimestampMs = parseInt(timestamp, 10) * 1000

                if (isNaN(chartTimestampMs) || !valueData || typeof valueData.value !== 'number' || isNaN(valueData.value)) {
                    return
                }

                const distance = Math.abs(chartTimestampMs - targetTimestampMs)

                if (!nearest || distance < nearest.distance) {
                    nearest = { x: chartTimestampMs, y: valueData.value, distance }
                }
            })

            return nearest ? { x: nearest.x, y: nearest.y } : null
        },
        formatMarkerTemperature(value) {
            const numericValue = Number(value)

            if (isNaN(numericValue)) {
                return '-'
            }

            if (Number.isInteger(numericValue)) {
                return formatDecimal(numericValue, i18n.locale, 0)
            }

            return formatDecimal(numericValue, i18n.locale, 2)
        },
        formatMarkerRelativeTime(timestampMs) {
            if (!timestampMs || isNaN(Number(timestampMs))) {
                return ''
            }

            const elapsedSeconds = Math.max(0, Math.floor((Date.now() - Number(timestampMs)) / 1000))
            const elapsedMinutes = Math.floor(elapsedSeconds / 60)
            const elapsedHours = Math.floor(elapsedMinutes / 60)
            const elapsedDays = Math.floor(elapsedHours / 24)

            if (elapsedSeconds < 60) {
                return `${elapsedSeconds}s`
            }

            if (elapsedMinutes < 60) {
                return `${elapsedMinutes}m`
            }

            if (elapsedHours < 24) {
                return `${elapsedHours}h`
            }

            if (elapsedDays > 0) {
                return `${elapsedDays}d`
            }
        },
        buildInteractiveMarkerLabel(point, isSelected) {
            return {
                borderColor: '#EAB308',
                style: {
                    color: 'var(--neutro-s-80-app, #2D2E2F)',
                    background: isSelected ? '#EAB308' : '#fff',
                    fontSize: '10px',
                    padding: { left: 4, right: 4, top: 2, bottom: 2 },
                    transform: 'rotate(-90deg)',
                    transformOrigin: 'center'
                },
                text: `${this.formatMarkerTemperature(point.y)}${this.chartMarkerUnit} · ${this.formatMarkerRelativeTime(point.x)}`,
                offsetY: -15,
                offsetX: 0
            }
        },
        buildInteractiveMarkerAnnotation(point, isSelected) {
            return {
                x: point.x,
                strokeDashArray: isSelected ? 0 : 5,
                borderColor: '#EAB308',
                borderWidth: 2,
                zIndex: isSelected ? 16 : 15,
                label: this.buildInteractiveMarkerLabel(point, isSelected)
            }
        },
        appendInteractiveAlertMarkers(result) {
            const selectedTimestamp = this.normalizeAlertMarkerTimestamp(this.selectedAlertTimestamp)
            const hoveredTimestamp = this.normalizeAlertMarkerTimestamp(this.hoveredAlertTimestamp)

            if (!selectedTimestamp && !hoveredTimestamp) {
                return result
            }

            const markerByTimestamp = {}

            if (selectedTimestamp) {
                const point = this.getMarkerPointAtTimestamp(selectedTimestamp)

                if (point) {
                    markerByTimestamp[selectedTimestamp] = this.buildInteractiveMarkerAnnotation(point, true)
                }
            }

            if (hoveredTimestamp && !markerByTimestamp[hoveredTimestamp]) {
                const point = this.getMarkerPointAtTimestamp(hoveredTimestamp)

                if (point) {
                    markerByTimestamp[hoveredTimestamp] = this.buildInteractiveMarkerAnnotation(point, false)
                }
            }

            result.xaxis = [...result.xaxis, ...Object.values(markerByTimestamp)]

            return result
        },
        hasChartSeries() {
            const values = this.sensorChartValues
            return !!values && typeof values !== 'string'
        },
        getAlertAnnotations() {
            const result = { points: [], xaxis: [] }

            if (!this.hasChartSeries()) {
                return result
            }

            const timeSpan = this.calculateTimeSpan()

            // Mostrar INCIDENCIAS (issues) en modo 'lines'
            if (this.alertMode === 'lines') {
                const issuesByPeriod = {}
                Object.entries(this.sensorChartValues).forEach(([timestamp, valueData]) => {
                    if (valueData && valueData.issue) {
                        const ts = parseInt(timestamp) * 1000
                        const date = new Date(ts)
                        let periodKey
                        switch (timeSpan) {
                            case 'hourly':
                                periodKey = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours()).getTime()
                                break
                            case 'daily':
                                periodKey = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
                                break
                            case 'monthly':
                                periodKey = new Date(date.getFullYear(), date.getMonth(), 1).getTime()
                                break
                            default:
                                periodKey = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours()).getTime()
                        }
                        const existing = issuesByPeriod[periodKey]
                        if (!existing) {
                            issuesByPeriod[periodKey] = { timestamp: ts, value: valueData.value, count: 1 }
                        } else {
                            if (ts > existing.timestamp) {
                                existing.timestamp = ts
                                existing.value = valueData.value
                            }
                            existing.count += 1
                        }
                    }
                })

                const entries = Object.values(issuesByPeriod)
                if (this.alertMode === 'lines') {
                    entries.forEach((issue) => {
                        const count = issue.count || 1
                        const labelText = count === 1 ? this.$t('sensor.issue') : `${count} ${this.$t('home.issues.title')}`
                        result.xaxis.push({
                            x: issue.timestamp,
                            strokeDashArray: 5,
                            borderColor: '#BD1F1E',
                            borderWidth: 2,
                            zIndex: 5,
                            label: {
                                borderColor: '#BD1F1E',
                                style: {
                                    color: '#fff',
                                    background: '#BD1F1E',
                                    fontSize: '10px',
                                    padding: { left: 4, right: 4, top: 2, bottom: 2 },
                                    transform: 'rotate(-90deg)',
                                    transformOrigin: 'center'
                                },
                                text: labelText,
                                offsetY: -15,
                                offsetX: 0
                            }
                        })
                    })
                }

                return this.appendSilencedStateAnnotations(this.appendInteractiveAlertMarkers(result))
            }

            // Mostrar ALERTAS (rangos por condición) en modo 'ranges'
            if (this.alertMode === 'ranges') {
                if (!this.sensor.config || !this.sensor.config.alert) {
                    return this.appendSilencedStateAnnotations(this.appendInteractiveAlertMarkers(result))
                }
                const alertConfig = this.sensor.config
                const allTimestamps = Object.keys(this.sensorChartValues)
                    .map((ts) => parseInt(ts) * 1000)
                    .sort((a, b) => a - b)
                const alertTimestamps = []
                Object.entries(this.sensorChartValues).forEach(([timestamp, valueData]) => {
                    const measurement = valueData.value
                    const hasAlert =
                        (valueData && (valueData.alert === 1 || valueData.alert === true)) ||
                        this.meetsAlertCondition(measurement, alertConfig)
                    if (hasAlert) {
                        const ts = parseInt(timestamp) * 1000
                        alertTimestamps.push({ timestamp: ts, value: measurement })
                    }
                })
                alertTimestamps.sort((a, b) => a.timestamp - b.timestamp)
                if (alertTimestamps.length > 0) {
                    const avgInterval = allTimestamps.length > 1 ? (allTimestamps[allTimestamps.length - 1] - allTimestamps[0]) / (allTimestamps.length - 1) : 3600000
                    const minRangeWidth = Math.max(avgInterval * 1.5, 300000)
                    const alertTsSet = new Set(alertTimestamps.map((a) => a.timestamp))

                    const pushRange = (startTs, endTs) => {
                        let x = startTs
                        let x2 = endTs
                        if (x2 <= x) {
                            x = startTs - minRangeWidth * 0.5
                            x2 = startTs + minRangeWidth * 0.5
                        }
                        result.xaxis.push({
                            x,
                            x2,
                            fillColor: 'rgba(189, 31, 30, 0.15)',
                            borderColor: 'rgba(189, 31, 30, 0.3)',
                            opacity: 0.7
                        })
                    }

                    const hasNonAlertBetween = (fromTs, toTs) => {
                        return allTimestamps.some((ts) => ts > fromTs && ts < toTs && !alertTsSet.has(ts))
                    }

                    const isInsideGap = (fromTs, toTs) => {
                        return this.gapBridges.some((b) => fromTs >= b.from.x - 1000 && toTs <= b.to.x + 1000)
                    }

                    let rangeStart = alertTimestamps[0]
                    let rangeEnd = alertTimestamps[0]
                    for (let i = 1; i < alertTimestamps.length; i++) {
                        const currentTs = alertTimestamps[i].timestamp
                        const prevTs = alertTimestamps[i - 1].timestamp

                        const canMerge =
                            !hasNonAlertBetween(prevTs, currentTs) && (currentTs - prevTs <= avgInterval * 3 || isInsideGap(prevTs, currentTs))
                        if (canMerge) {
                            rangeEnd = alertTimestamps[i]
                        } else {
                            pushRange(rangeStart.timestamp, rangeEnd.timestamp)
                            rangeStart = alertTimestamps[i]
                            rangeEnd = alertTimestamps[i]
                        }
                    }
                    pushRange(rangeStart.timestamp, rangeEnd.timestamp)
                }
            }

            return this.appendSilencedStateAnnotations(this.appendInteractiveAlertMarkers(result))
        },
        parseHistoryTimestamp(time) {
            if (time === null || typeof time === 'undefined' || time === '') {
                return null
            }

            const parsed = moment.utc(time)
            if (!parsed.isValid()) {
                return null
            }

            return parsed.valueOf()
        },
        getSilencedStateAnnotations() {
            const ranges = []
            const markers = []
            const chartRange = this.getFullXRange()

            if (!chartRange || chartRange.max <= chartRange.min) {
                return { ranges, markers }
            }

            const events = (Array.isArray(this.sensorHistory) ? this.sensorHistory : [])
                .map((item) => ({
                    time: this.parseHistoryTimestamp(item && item.time),
                    on: !!(item && item.reason === 'On')
                }))
                .filter((item) => item.time !== null)
                .sort((a, b) => a.time - b.time)

            const clamp = (timestamp) => Math.min(chartRange.max, Math.max(chartRange.min, timestamp))

            events.forEach((event) => {
                if (event.time < chartRange.min || event.time > chartRange.max) {
                    return
                }

                markers.push(this.buildSilencedMarker(event.time, event.on))
            })

            // Shade only from on/off history. The live toggle is not the state of this range.
            let silencedFrom = null
            if (events.length > 0 && events[0].on && events[0].time > chartRange.min) {
                silencedFrom = chartRange.min
            }

            const closePeriod = (end) => {
                if (silencedFrom === null) {
                    return
                }

                const x = clamp(silencedFrom)
                const x2 = clamp(end)
                silencedFrom = null

                if (x2 <= x) {
                    return
                }

                ranges.push({
                    x,
                    x2,
                    fillColor: 'rgba(45, 46, 47, 0.12)',
                    borderColor: 'transparent',
                    opacity: 1
                })
            }

            events.forEach((event) => {
                if (event.on) {
                    closePeriod(event.time)
                    return
                }

                if (silencedFrom === null && event.time < chartRange.max) {
                    silencedFrom = Math.max(event.time, chartRange.min)
                }
            })

            if (silencedFrom !== null) {
                closePeriod(chartRange.max)
            }

            return { ranges, markers }
        },
        buildSilencedMarker(timestamp, isOn) {
            return {
                x: timestamp,
                strokeDashArray: 5,
                borderColor: '#6B7280',
                borderWidth: 2,
                zIndex: 4,
                label: {
                    borderColor: '#6B7280',
                    style: {
                        color: '#fff',
                        background: '#6B7280',
                        fontSize: '10px',
                        padding: { left: 4, right: 4, top: 2, bottom: 2 },
                        transform: 'rotate(-90deg)',
                        transformOrigin: 'center'
                    },
                    text: isOn ? this.$t('sensor.history.on') : this.$t('sensor.history.off'),
                    offsetY: -15,
                    offsetX: 0
                }
            }
        },
        appendSilencedStateAnnotations(result) {
            const silenced = this.getSilencedStateAnnotations()
            result.xaxis = [...result.xaxis, ...silenced.ranges, ...silenced.markers]
            return result
        },
        meetsAlertCondition(value, config) {
            if (!config || !config.condition || (config.value == null && config.value !== 0)) return false

            const numericValue = parseFloat(value)
            if (isNaN(numericValue)) return false

            const condition = String(config.condition).trim()
            const alertValue = config.value

            if (condition === '<>' || condition === '!<>') {
                const rangeStr = typeof alertValue === 'string' ? alertValue : String(alertValue)
                if (rangeStr.includes(',')) {
                    const parts = rangeStr.split(',').map((v) => parseFloat(String(v).trim()))
                    const min = Math.min(parts[0], parts[1])
                    const max = Math.max(parts[0], parts[1])
                    if (isNaN(min) || isNaN(max)) return false

                    const isInside = numericValue >= min && numericValue <= max
                    return condition === '<>' ? isInside : !isInside
                }
                return false
            }

            const threshold = parseFloat(alertValue)
            if (isNaN(threshold) && condition !== '!empty') return false

            const comparisons = {
                '<': (v, a) => v < a,
                '>': (v, a) => v > a,
                '<=': (v, a) => v <= a,
                '>=': (v, a) => v >= a,
                '==': (v, a) => v === a,
                '=': (v, a) => v === a
            }

            return comparisons[condition] ? comparisons[condition](numericValue, threshold) : false
        },
        initChart() {
            this.show = false
            if (this.sensorChartValues) {
                this.xAxisValues()
            }
        },
        formatDate(date) {
            if (typeof date === 'number' || (typeof date === 'string' && !isNaN(date))) {
                const m = moment.unix(Number(date))
                return formatDisplayDateFromStore(m, { includeTime: true, emptyValue: null })
            }

            return formatDisplayDateFromStore(date, { includeTime: true, emptyValue: null })
        },
        toggleMode(mode) {
            // Si el modo clickeado ya está activo, desactivarlo
            if (this.alertMode === mode) {
                this.alertMode = null
                this.saveAlertMode('')
            } else {
                // Si no, activar el nuevo modo (desactivando automáticamente el anterior)
                this.alertMode = mode
                this.saveAlertMode(mode)
            }

            // Solo actualizar las opciones del gráfico sin recrear el componente
            this.updateChartOptions()
        },

        updateChartOptions() {
            // Actualizar solo las opciones del gráfico sin recrear el componente
            if (this.hasChartSeries()) {
                // Solo actualizar las anotaciones sin recrear el gráfico completo
                this.updateAnnotationsOnly()
            }
        },

        updateAnnotationsOnly() {
            // Método optimizado para actualizar solo las anotaciones
            if (!this.hasChartSeries()) return

            // Obtener los valores actuales del eje
            const min = this.getCurrentMinValue()
            const max = this.getCurrentMaxValue()
            const hasAmbient = this.hasAmbientSeries
            const hasAlerts = this.sensor.config && this.sensor.config.alert
            const hasTwoAlerts = hasAlerts && typeof this.sensor.config.value === 'string' && this.sensor.config.value.includes(',')

            // Actualizar solo las anotaciones sin ocultar el gráfico
            this.updateAxis(min, max, hasAmbient, hasAlerts, hasTwoAlerts)
        },

        getCurrentMinValue() {
            if (!this.sensorChartValues || typeof this.sensorChartValues === 'string') return 0

            let min = 9000
            Object.values(this.sensorChartValues).forEach((value) => {
                if (value && typeof value.value === 'number' && !isNaN(value.value)) {
                    if (value.value < min) min = value.value
                }
                if (this.hasDualTemperatureSeries && (typeof value.ambient !== 'undefined' || typeof value.aambiente !== 'undefined')) {
                    const ambientRaw = typeof value.ambient !== 'undefined' ? value.ambient : value.aambiente
                    const ambientNum = parseFloat(ambientRaw)
                    if (!isNaN(ambientNum) && ambientNum < min) {
                        min = ambientNum
                    }
                }
            })

            if (this.sensor && this.sensor.config && this.sensor.config.value != null) {
                const alertVals = typeof this.sensor.config.value === 'string'
                    ? this.sensor.config.value.split(',').map((v) => parseFloat(v)).filter((v) => !isNaN(v))
                    : [parseFloat(this.sensor.config.value)].filter((v) => !isNaN(v))
                alertVals.forEach((v) => {
                    if (v < min) min = v
                })
            }

            return min === 9000 ? 0 : Math.round(min) - 1
        },

        getCurrentMaxValue() {
            if (!this.sensorChartValues || typeof this.sensorChartValues === 'string') return 10

            let max = -1000
            Object.values(this.sensorChartValues).forEach((value) => {
                if (value && typeof value.value === 'number' && !isNaN(value.value)) {
                    if (value.value > max) max = value.value
                }
                if (this.hasDualTemperatureSeries && (typeof value.ambient !== 'undefined' || typeof value.aambiente !== 'undefined')) {
                    const ambientRaw = typeof value.ambient !== 'undefined' ? value.ambient : value.aambiente
                    const ambientNum = parseFloat(ambientRaw)
                    if (!isNaN(ambientNum) && ambientNum > max) {
                        max = ambientNum
                    }
                }
            })

            if (this.sensor && this.sensor.config && this.sensor.config.value != null) {
                const alertVals = typeof this.sensor.config.value === 'string'
                    ? this.sensor.config.value.split(',').map((v) => parseFloat(v)).filter((v) => !isNaN(v))
                    : [parseFloat(this.sensor.config.value)].filter((v) => !isNaN(v))
                alertVals.forEach((v) => {
                    if (v > max) max = v
                })
            }

            return max === -1000 ? 10 : Math.round(max) + 1
        },

        getXAxisRange() {
            const now = Date.now()

            if (!this.dateFilter || !this.dateFilter.type_date) {
                return null
            }

            let start = null
            let end = null

            switch (this.dateFilter.type_date) {
                case 'today':
                    start = moment().subtract(24, 'hours')
                    end = moment()
                    break
                case 'yesterday':
                    start = moment().subtract(1, 'day').startOf('day')
                    end = moment().subtract(1, 'day').endOf('day')
                    break
                case 'week':
                    start = moment().subtract(1, 'week').startOf('day')
                    end = moment()
                    break
                case 'month':
                    start = moment().subtract(1, 'month').startOf('day')
                    end = moment()
                    break
                case 'quarter':
                    start = moment().subtract(3, 'months').startOf('day')
                    end = moment()
                    break
                case 'custom':
                    if (!this.dateFilter.date || !this.dateFilter.date.start_date || !this.dateFilter.date.end_date) {
                        return null
                    }
                    start = moment(this.dateFilter.date.start_date).startOf('day')
                    end = moment(this.dateFilter.date.end_date).endOf('day')
                    break
                default:
                    return null
            }

            if (!start || !end || !start.isValid() || !end.isValid()) {
                return null
            }

            return [start.valueOf(), Math.min(end.valueOf(), now)]
        },

        getXAxisMin() {
            const range = this.getXAxisRange()
            return range ? range[0] : null
        },

        getXAxisMax() {
            const range = this.getXAxisRange()
            return range ? range[1] : null
        },

        toggleAnimations() {
            // Guardar preferencia en localStorage
            localStorage.setItem('sensorChart_enableAnimations', this.enableAnimations.toString())

            // Aplicar cambios inmediatamente si hay datos
            if (this.sensorChartValues) {
                this.updateChartOptions()
            }
        },

        toggleGradient() {
            // Guardar preferencia en localStorage
            localStorage.setItem('sensorChart_hideGradient', this.hideGradient.toString())

            // Aplicar cambios inmediatamente si hay datos
            if (this.sensorChartValues) {
                this.updateChartOptions()
            }
        },

        toggleGrid() {
            // Guardar preferencia en localStorage
            localStorage.setItem('sensorChart_showGrid', this.showGrid.toString())

            // Aplicar cambios inmediatamente si hay datos
            if (this.sensorChartValues) {
                this.updateChartOptions()
            }
        },

        toggleReverseYAxis() {
            // Guardar preferencia en localStorage
            localStorage.setItem('sensorChart_reverseYAxis', this.reverseYAxis.toString())

            // Aplicar cambios inmediatamente si hay datos
            if (this.sensorChartValues) {
                this.updateChartOptions()
            }
        },

        loadUserPreferences() {
            // Cargar preferencia de animaciones
            const savedAnimations = localStorage.getItem('sensorChart_enableAnimations')
            if (savedAnimations !== null) {
                this.enableAnimations = savedAnimations === 'true'
            } else {
                // Guardar valor por defecto en localStorage
                localStorage.setItem('sensorChart_enableAnimations', this.enableAnimations.toString())
            }

            // Cargar preferencia de gradient
            const savedGradient = localStorage.getItem('sensorChart_hideGradient')
            if (savedGradient !== null) {
                this.hideGradient = savedGradient === 'true'
            } else {
                // Guardar valor por defecto en localStorage
                localStorage.setItem('sensorChart_hideGradient', this.hideGradient.toString())
            }

            // Cargar preferencia de grid
            const savedGrid = localStorage.getItem('sensorChart_showGrid')
            if (savedGrid !== null) {
                this.showGrid = savedGrid === 'true'
            } else {
                // Guardar valor por defecto en localStorage
                localStorage.setItem('sensorChart_showGrid', this.showGrid.toString())
            }

            // Cargar preferencia de inversión del eje Y
            const savedReverseYAxis = localStorage.getItem('sensorChart_reverseYAxis')
            if (savedReverseYAxis !== null) {
                this.reverseYAxis = savedReverseYAxis === 'true'
            } else {
                // Guardar valor por defecto en localStorage
                localStorage.setItem('sensorChart_reverseYAxis', this.reverseYAxis.toString())
            }

            // Cargar preferencia de modo de alerta
            const savedMode = localStorage.getItem('sensorChart_alertMode')
            if (savedMode && ['lines', 'ranges'].includes(savedMode)) {
                this.alertMode = savedMode
            } else {
                // Guardar valor por defecto en localStorage
                localStorage.setItem('sensorChart_alertMode', this.alertMode)
            }
        },

        saveAlertMode(mode) {
            // Guardar preferencia del modo de alerta
            localStorage.setItem('sensorChart_alertMode', mode)
        },

        toggleSettingsDropdown() {
            this.showSettingsDropdown = !this.showSettingsDropdown
        },

        closeSettingsDropdown() {
            this.showSettingsDropdown = false
        },

        handleClickOutside(event) {
            // Cerrar dropdown si se hace click fuera del componente
            if (this.showSettingsDropdown && !this.$el.contains(event.target)) {
                this.showSettingsDropdown = false
            }
        }
    },
    mounted() {
        // Cargar preferencias del usuario
        this.loadUserPreferences()
        if (this.sensor.config == false) {
            this.configDisabled = true;
        } else {
            this.alertEnabled = this.sensor.config.alert;
        }
        // Listener para cerrar dropdown al hacer click fuera
        document.addEventListener('click', this.handleClickOutside)

        let self = this
        setTimeout(() => {
            self.show = true
        }, 200)
        if (this.sensorChartValues) {
            this.xAxisValues()
        }
    },
    updated() {
        this.bindChartGestureListeners()
    },

    beforeDestroy() {
        this.unbindChartGestureListeners()
        this.endTouchGesture()
        document.removeEventListener('click', this.handleClickOutside)
    },
    watch: {
        sensorChartValues(newValue) {
            this.resetZoomState()
            this.show = false
            if (newValue) {
                this.xAxisValues()
            }
        },
        dateFilter: {
            handler() {
                this.resetZoomState()
            },
            deep: true
        },
        hoveredAlertTimestamp() {
            this.updateChartOptions()
        },
        selectedAlertTimestamp() {
            this.updateChartOptions()
        },
        sensorHistory() {
            if (this.chartOptions && this.show && this.hasChartSeries()) {
                this.updateChartOptions()
            }
        }
    }
}
</script>

<style lang="scss">
.sensorchart-justify {
    .justify-info {
        @include display-flex();
        width: 100% !important;
        padding: 0 !important;
        gap: 12px;

        .step {
            width: 100% !important;
            ul {
                list-style: none !important;
                padding: 0 !important;
                margin: 0 !important;
            }
        }
    }
    .pop-footer .buttons {
        flex-direction: column-reverse !important;
        button {
            width: 100% !important;
        }
    }
    .kind-of {
        padding: 0px !important;
        margin: 0px !important;
    }
}
.sensor-chart-plot {
    position: relative;
    width: 100%;
    touch-action: pan-y;
    overscroll-behavior: contain;

    .apexcharts-canvas {
        touch-action: inherit;
    }

    &.is-zoomed {
        touch-action: none;

        .apexcharts-canvas {
            touch-action: none;
        }
    }

    .chart-reset-zoom {
        position: absolute;
        top: 8px;
        right: 16px;
        z-index: 2;
        padding: 6px 10px;
        border-radius: 4px;
        border: 1px solid #c4ccd9;
        background: #fff;
        color: #2f2e30;
        cursor: pointer;
        font-family: 'Avenir-Regular', sans-serif;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.2;

        &:hover,
        &:focus {
            background: #eaeef9;
            border-color: #2c52c4;
            outline: none;
        }
    }
}

.chart-container {
    width: 100%;
    height: auto;
    margin: 0 auto;
    background-color: #fff;
    border-radius: 4px;
    border: 1px solid #c4ccd9;
    overflow: hidden;

    .greybox {
        height: 250px;
        @include background($image: img('loader_dark.svg'), $position: center center, $size: 50px, $color: #fff);
        margin-bottom: 12px;
    }
}

.chart-content {
    width: 100%;
    @include display-flex();
    @include flex-direction(column);

    .sensor-name-container,
    .sensor-ref-container {
        @include display-flex();
        @include flex-direction(row);
        @include justify-content(space-between);
        @include align-items(center);

        .sensor-name-container-content {
            display: flex;
            flex-direction: row;
            align-content: center;
            flex-wrap: nowrap;
            justify-content: center;
            align-items: center;
        }

        .box-sensor-name {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }

        .sensor-name {
            color: var(--neutro-s-80-dashboard, #2f2e30);
            text-align: center;
            font-family: 'Avenir-Bold';
            font-size: 20px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
        }
        .sensor-ref {
            color: var(--neutro-s-60-app, #5b5c5f);
            text-align: center;
            font-family: 'Avenir-Regular';
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
        }
        .alert-configuration {
            @include display-flex();
            @include flex-direction(row);
            @include align-items(center);
            gap: 8px;
            margin: 16px 0 8px 0;
            margin-left: auto;
            padding: 8px;
            border-radius: 4px;
            &.online {
                background-color: #eaeef9;
                .i-andy-info-main {
                    color: #2c52c4;
                }
            }
            &.offline {
                background-color: #fcefef;
                .i-andy-info-main {
                    color: $error-dark;
                }
            }

            .sensor-alert-stabilizing > b {
                color: var(--neutro-s-80-app, #2D2E2F);
                font-family: "Avenir-Bold";
                @include font-size(16);
                font-style: normal;
                font-weight: 700;
                line-height: 20px; /* 125% */
                letter-spacing: 0.25px;
            }

            label {
                margin: 0;
            }
            .switch {
                position: relative;
                display: inline-block;
                width: 32px;
                height: 18px;

                .slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: $color-neutral-600;
                    -webkit-transition: 0.4s;
                    transition: 0.4s;
                }

                .slider:before {
                    position: absolute;
                    content: '';
                    height: 14px;
                    width: 14px;
                    left: 2px;
                    top: 2px;
                    background-color: white;
                    -webkit-transition: 0.4s;
                    transition: 0.4s;
                }

                input:checked + .slider {
                    background-color: $color-primary-500;
                }

                input:focus + .slider {
                    box-shadow: 0 0 1px $done;
                }

                input:checked + .slider:before {
                    -webkit-transform: translateX(14px);
                    -ms-transform: translateX(14px);
                    transform: translateX(14px);
                }

                .slider.round {
                    border-radius: 18px;
                }

                .slider.round:before {
                    border-radius: 50%;
                }
            }

            .switch input {
                display: none;
            }
        }
    }

    .sensor-value-container {
        @include display-flex();
        @include flex-direction(row);
        @include justify-content(space-between);
        @include align-items(center);
        padding: 0 16px 8px 16px;

        .sensor-value {
            color: #222;
            text-align: center;
            font-family: "Avenir-Bold";
            font-size: 24px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;

            .sensor-value-humidity {
                display: inline-flex;
                align-items: center;
                gap: 4px;

                .i-andy-droplets {
                    flex-shrink: 0;
                    color: $color-info-500;
                }

                .sensor-value-humidity-text,
                .sensor-value-humidity-unit {
                    color: #222;
                    text-align: center;
                    font-family: "Avenir-Bold";
                    font-size: 24px;
                    font-style: normal;
                    font-weight: 700;
                    line-height: normal;
                }
            }
        }
        .sensor-value-unit {
            color: #222;
            text-align: center;
            font-family: "Avenir-Regular";
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
        }
    }

    .sensor-name-container {
        @include display-flex();
        @include flex-direction(row);
        @include justify-content(flex-start);
        @include align-items(center);
        padding: 0 16px;

        .icon-status {
            margin-right: 8px;
            border-radius: 2px;
            padding: 4px;

            &.alert {
                color: #ffc700;
                background: var(--warning-t-90-app, #fff8e8);

            }
            &.issue {
                color: #bd1f1e;
                background: var(--error-t-90-app, #fcefef);
            }
            &.working {
                color: #4caf50;
                background: var(--success-t-90-app, #eaf9f0);
            }
            &.nodata {
                color: #bd1f1e;
            }
            &.offline {
                color: $neutro-s30;
                background-color: $neutro-t80;

            }
        }

        .date-container {
            @include display-flex();
            @include flex-direction(column);
            @include justify-content(center);
            @include align-items(flex-start);

            .history {
                color: $color-black;
                font-size: 16px;
                font-weight: 700;
            }

            .last-value {
                color: #5b5c5f;
                font-size: 12px;
                font-weight: 400;
            }
        }
    }

    .alert-mode-toggles {
        @include display-flex();
        @include flex-direction(column);
        padding: 12px 16px;
        gap: 12px;

        .alert-mode-tabs {
            @include display-flex();
            @include flex-direction(row);
            @include align-items(center);
            gap: 8px;

            .tab {
                padding: 8px;
                border-radius: 4px;
                border: 1px solid $neutro;
                background: $neutro-light;
                color: #373d3f;
                cursor: pointer;
                @include font-size(12px);
                &.active {
                    background: $main-t90;
                    border-color: $main-t90;
                }
            }

            .chart-settings-dropdown {
                position: relative;
                margin-left: auto;

                .settings-trigger {
                    padding: 8px;
                    border-radius: 4px;
                    border: 1px solid $neutro;
                    background: $neutro-light;
                    color: #373d3f;
                    cursor: pointer;
                    @include font-size(12px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 32px;
                    height: 32px;

                    &:hover {
                        background: $main-t90;
                        border-color: $main-t90;
                        color: white;
                    }

                    .three-dots-vertical {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        gap: 2px;

                        span {
                            width: 3px;
                            height: 3px;
                            background-color: #373d3f;
                            border-radius: 50%;
                            display: block;
                        }
                    }
                }

                .settings-menu {
                    position: absolute;
                    top: 100%;
                    right: 0;
                    background: white;
                    border: 1px solid #e5e5e5;
                    border-radius: 4px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                    z-index: 1000;
                    min-width: 200px;
                    padding: 8px;
                    margin-top: 4px;

                    .checkbox-container {
                        padding: 8px;
                        border-radius: 4px;

                        &:hover {
                            background: #f5f5f5;
                        }
                    }
                }
            }

            .checkbox-container {
                @include display-flex();
                @include flex-direction(row);
                @include align-items(center);
                cursor: pointer;
                gap: 8px;

                input[type='checkbox'] {
                    display: none;
                }

                .checkmark {
                    width: 16px;
                    height: 16px;
                    border: 2px solid $neutro;
                    border-radius: 3px;
                    background: white;
                    position: relative;
                    transition: all 0.2s ease;

                    &::after {
                        content: '';
                        position: absolute;
                        left: 25%;
                        width: 4px;
                        height: 8px;
                        border: solid white;
                        border-width: 0 2px 2px 0;
                        transform: rotate(45deg);
                        opacity: 0;
                        transition: opacity 0.2s ease;
                    }
                }

                input[type='checkbox']:checked + .checkmark {
                    background: $color-primary-300;
                    border-color: $color-primary-300;

                    &::after {
                        opacity: 1;
                    }
                }

                .checkbox-label {
                    font-size: 12px;
                    color: #373d3f;
                    font-weight: 400;
                }
            }
        }

        .chart-options {
            @include display-flex();
            @include flex-direction(row);
            @include align-items(center);
            gap: 8px;
        }
    }
    .history-container {
        padding-left: 16px;

        .date-container {
            display: flex;
            flex-direction: column;
            align-items: flex-start;

            .history {
                color: #222;
                text-align: center;
                font-family: 'Avenir-Bold';
                font-size: 16px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
            }
            .last-value {
                color: var(--neutro-s-60-app, #5B5C5F);
                text-align: center;
                font-family: 'Avenir-Regular';
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
            }
        }
    }
}
.sensor-name-container.portrait {
    margin-top: 16px;
    flex-direction: column !important;
    align-items: flex-start !important;
    .alert-configuration {
        width: 100%;
        span {
            display: block;
            width: 100%;
            word-break: break-word;
            white-space: normal;
            font-size: 14px !important;
        }
        .switch {
            flex-shrink: 0;
        }
    }
}
.sensorchart-justify.popup-container.portrait {
    align-items: center !important;
}
</style>
